/**
 * The airlock's two doors: back out to it, and through it into either fab.
 *
 * LEAVING. The menu asks for it; the composition root does it, because doing
 * it touches the player, the panel and talk stores and the saved place, all
 * of which live in main.tsx and none of which a menu item should reach into.
 * No reload: the hall is already built, and a student who wants the door
 * again wants the door, not a fifteen-second rebuild of the room behind it.
 *
 * ENTERING THE OTHER FAB. The hall is fixed for the page (fab/hall.ts), so
 * the other one is a reload away. A student who chose it from the airlock
 * has already read the airlock; making them read it again on the far side
 * would be a toll, not a ritual. So the choice leaves a note for the next
 * page - session-scoped, so it cannot outlive the tab or leak into another -
 * and that page walks them straight in.
 */

import { showLoadingThen } from './loading'
import { setHall, type HallLayout } from '../../fab/hall'

let leave: (() => void) | null = null

/** Once, at boot: the routine that puts the student back at the airlock. */
export function bindLeaveToEntrance(fn: () => void): void {
  leave = fn
}

export function leaveToEntrance(): void {
  leave?.()
}

const ENTER_KEY = 'simfab.enterOnLoad'

/** From the airlock, into the fab this page was NOT built as. */
export function enterOtherFab(id: HallLayout): void {
  try {
    sessionStorage.setItem(ENTER_KEY, '1')
  } catch {
    // Then the far side shows its airlock once more. Not the end of the world.
  }
  // The screen goes up and is painted before setHall rebuilds the hall in
  // place - the rebuild blocks the thread, and a screen shown in the same
  // task as the block is never seen (loading.ts showLoadingThen).
  showLoadingThen(id, () => setHall(id))
}

/**
 * Whether the previous page asked for a straight walk-in. Consumed on read,
 * so a later refresh gets the airlock like anyone else.
 */
export function takeEnterOnLoad(): boolean {
  try {
    const on = sessionStorage.getItem(ENTER_KEY) === '1'
    if (on) sessionStorage.removeItem(ENTER_KEY)
    return on
  } catch {
    return false
  }
}
